(ns manatee.admin.task-config.view
  "The task add/edit and summary form components."
  (:require
   [cljs-time.format :as time-format]
   [manatee.admin.subs :as admin-subs]
   [manatee.admin.task-config.documents.handlers :as task-documents-handlers]
   [manatee.admin.task-config.documents.view :as task-documents-view]
   [manatee.admin.task-config.form-components :as form-components]
   [manatee.admin.task-config.handlers :as task-config-handlers]
   [manatee.admin.task-config.subs :as task-config-subs]
   [manatee.admin.view :as admin-view]
   [manatee.components.notifications.container :as notifications]
   [manatee.domain.task :as task-domain]
   [manatee.ui.templates :as templates]
   [manatee.user.components :as user-components]
   [manatee.user.subs :as user-subs]
   [medley.core :as medley]
   [re-frame.core :as rf]
   [rothko.button.component :refer [button]]
   [rothko.form2.checkbox.component :as checkbox]
   [rothko.form2.component :as form]
   [rothko.form2.input.component :as input]
   [rothko.form2.select.multi-select.component :as multi-select]
   [rothko.form2.select.single-select.component :as single-select]
   [rothko.form2.validation :as validation]
   [rothko.layout.component :refer [layout]]
   [rothko.panel.component :refer [panel]]))

(defn- form-values->components
  "Return a map of task form field name to the corresponding form component."
  [props {:keys [modifiers] :as form-values}]
  (let [form-components (medley/map-kv-vals (fn [name component-fn]
                                              (-> (component-fn props form-values)
                                                  (assoc ::form/name name)))
                                            form-components/task-components)
        modifier-components (medley/map-kv-vals (fn [name component-fn]
                                                  (-> (component-fn (modifiers name))
                                                      (assoc ::form/name name)))
                                                form-components/modifier-components)]
    (merge form-components modifier-components)))

(defn task-form
  "The task add/edit form."
  [props values components]
  (let [{:keys [form-name on-submit submit-button upload-button]} components]
    [:div
     [form/form
      {::form/name form-name
       ::form/on-submit* on-submit}
       ; For part-rejection-options and answer-rejection-options, the select
       ; components for both speech and text are created but only one medium is
       ; displayed depending on the selected type"
      (form-values->components props values)
      (fn [{:keys [::form/submit! ::form/get-submitting? ::validation/valid? ::form/value]}
           {:keys [allow-deferments? allow-going-back? allow-notes? allow-reports? archive-task? avoid-rejected-items? hide-media?
                   multi-marking? multi-reject? reject-with-annotation? released-submissions-only? show-ai-hybrid? show-analytic-scores?
                   show-answers-remaining? show-long-rubric? show-short-rubric? suppress-first-iterations? suppress-part-screen?

                   answer-score-type assessment-name custom-validation-rules
                   field-type first-stage-rejections next-stage-answer-rejections
                   next-stage-part-rejections part-score-type
                   answer-rejection-options-speech answer-rejection-options-text
                   part-rejection-options-speech part-rejection-options-text
                   score-range-from score-range-to scoring-criteria
                   source task-id task-name team-id] :as _field-props}]
        (let [current-field-type (:field-type value)
              script-annotation? (= :script-annotation current-field-type)
              show-part-screen? (not (:suppress-part-screen? value))
              show-part-rejections-speech? (and show-part-screen? (not script-annotation?))
              show-part-rejections-text? (and show-part-screen? script-annotation?)
              show-answer-rejections-speech? (not script-annotation?)
              show-answer-rejections-text? script-annotation?
              show-upload-button (or (= :data-uploaded-on-creation (:source value))
                                     (task-domain/released-submission-upload? value)
                                     (task-domain/aaet-upload? value))]
          [:div
           [:div.form-row.button-group
            [button {:appearance #{:tertiary}
                     :type :button
                     :on-click #(rf/dispatch [:navigate-to [:manatee.admin.handlers/admin {:query-params {:tab (name :tasks)}}]])}
             "Back"]
            (when (= form-name :task-details-form)
              (let [task-config-id @(rf/subscribe [::task-config-subs/task-config-id])]
                [button {:appearance #{:tertiary}
                         :style {:margin-left "8px"}
                         :type :button
                         :on-click #(rf/dispatch [:navigate-to [:manatee.task-config.handlers/task-summary :task-config-id task-config-id]])}
                 "Summary"]))
            (when (= form-name :task-details-form)
              (let [task-config-id @(rf/subscribe [::task-config-subs/task-config-id])]
                [button {:appearance #{:tertiary}
                         :style {:margin-left "8px"}
                         :type :button
                         :on-click #(rf/dispatch [::task-documents-handlers/open-task-documents-view task-config-id])}
                 "Documentation"]))]
           [layout {:appearance #{:row}
                    :class [:task-page__form-layout]}
            [:div {:class [:task-page__left-column]}
             [input/field task-id]
             [input/field task-name]
             [single-select/field team-id]
             [single-select/field source]
             [single-select/field field-type]
             (if show-part-screen?
               [single-select/field part-score-type] "")
             [single-select/field answer-score-type]
             (if script-annotation?
               [single-select/field assessment-name] "")
             (if (and (= current-field-type :scoring)
                      (or (= (:answer-score-type value) :none)
                          (= (:answer-score-type value) nil)))
               [:div
                [multi-select/field scoring-criteria]
                [:div.task-page__score-range
                 [input/field score-range-from]
                 [input/field score-range-to]]]
               "")
             (if show-part-rejections-speech? [multi-select/field part-rejection-options-speech] "")
             (if show-part-rejections-text? [multi-select/field part-rejection-options-text] "")
             (if show-answer-rejections-speech? [multi-select/field answer-rejection-options-speech] "")
             (if show-answer-rejections-text? [multi-select/field answer-rejection-options-text] "")
             [multi-select/field first-stage-rejections]
             [multi-select/field next-stage-part-rejections]
             [multi-select/field next-stage-answer-rejections]
             [multi-select/field custom-validation-rules]]
            [:div
           ;; See https://btsservice.atlassian.net/browse/ELIT-3605.
           ;; The options are ordered alphabetically (by label - see the
           ;; corresponding form component definitions); additions should
           ;; adhere to this ordering.
             [:h4 "Options"]
             [checkbox/field allow-deferments?]
             [checkbox/field allow-going-back?]
             [checkbox/field allow-reports?]
             [checkbox/field allow-notes?]
             [checkbox/field archive-task?]
             [checkbox/field avoid-rejected-items?]
             [checkbox/field hide-media?]
             [checkbox/field multi-marking?]
             [checkbox/field multi-reject?]
             [checkbox/field reject-with-annotation?]
             [checkbox/field released-submissions-only?]
             [checkbox/field show-analytic-scores?]
             [checkbox/field show-answers-remaining?]
             [checkbox/field show-long-rubric?]
             [checkbox/field show-short-rubric?]
             [checkbox/field show-ai-hybrid?]
             [checkbox/field suppress-first-iterations?]
             [checkbox/field suppress-part-screen?]]]

           [:div {:class [:task-page__form-layout
                          :task-page__space-rows]}
            (if show-upload-button
              upload-button
              [:p])
            (when submit-button
              [button (assoc (:button submit-button) :on-click submit!
                             :loading? (get-submitting?)
                             :disabled (not valid?))
               (:label submit-button)])]]))]]))

(defn summary
  "The task summary form."
  [props]
  (let [admin? @(rf/subscribe [::user-subs/admin?])
        project-owner? @(rf/subscribe [::user-subs/project-owner?])
        users (->> @(rf/subscribe [::admin-subs/users])
                   (group-by :id)
                   (medley/map-vals first))
        task-config-id @(rf/subscribe [::task-config-subs/task-config-id])
        {:keys [task-name] :as task} @(rf/subscribe [::task-config-subs/task-config task-config-id])
        task-summary @(rf/subscribe [::admin-subs/task-summary])
        released-submissions-only? (task-domain/modifier task :released-submissions-only?)
        multi-marking? (task-domain/modifier task :multi-marking?)]
    (if (or admin? project-owner?)
      [:<>
       [notifications/alerts {:banner-id :main
                              :max-alerts 1}]
       [panel {:appearance #{:primary}
               :class [:task-page__panel]}
        [layout {:appearance #{:column}
                 :class [:task-page__form-layout]}
         [:h2 (str "Task summary for " task-name)]
         [:div.form-row.button-group
          [button {:appearance #{:tertiary}
                   :type :button
                   :on-click #(rf/dispatch [:navigate-to [:manatee.task-config.edit.handlers/edit-task :task-config-id task-config-id]])}
           "Back"]]
         (let [{:keys [submissions-released submissions-accounted
                       parts-completed first-edit-created last-edit-created]} task-summary
               formatter (time-format/formatter "HH:mm' GMT, 'dd/MMM/yyyy")
               info (fn [datum] (cond (nil? datum) "Not available"
                                      (instance? goog.date.UtcDateTime datum) (time-format/unparse formatter datum)
                                      :else (str datum)))
               tally (fn [p] (reduce (fn [sum {c :count}] (+ sum c)) 0 p))
               aggregate (fn [field formatter]
                           (->> parts-completed
                                (group-by field)
                                (medley/map-vals tally)
                                (map formatter)))
               completed-table (if multi-marking?
                                 (aggregate :user-id
                                            (fn [[user-id count]]
                                              [:tr [:td (str count)] [:td (user-components/full-name (get users user-id))]]))
                                 (aggregate :status
                                            (fn [[status count]]
                                              [:tr [:td (str count)] [:td (name status)]])))]
           [:div {:style {:display :grid
                          :grid-template-columns "15em auto"
                          :gap "16px"}}
            [:label "Number of submissions"]
            [:span (info (if (pos? submissions-released) submissions-released submissions-accounted))]
            [:label "Number of completed parts"]
            (if (seq completed-table) [:table (into [:tbody] completed-table)] [:span "None"])
            [:label "First submission date"]
            [:span (info first-edit-created)]
            [:label "Latest submission date"]
            [:span (info last-edit-created)]])]
        (when released-submissions-only?
          [button {:appearance #{:secondary}
                   :type :button
                   :on-click #(rf/dispatch [::task-config-handlers/task-results task-config-id])}
           "Download Results"])]]
      [admin-view/access-denied props])))

(def summary-page
  {:main summary
   :template templates/single-column})

(def documents-page
  {:main task-documents-view/view-container
   :template templates/single-column})
